import React, { useEffect, useState, useRef } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import Layout from "../layout/Layout";
import ContentWrapper from "../components/ContentWrapper";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  bookinlist,
  acceptCancellation,
} from "../Redux/Action/BookingAction";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import Pagination from "../Component/Pagination";
import FirstLetterCapital from "../CustomHooks/FirstLetterCapital";
import LoadingOverlay from "react-loading-overlay-ts";
import defaultAvatar from "../assets/images/default-avatar.png";

export default function Booking() {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [statusFilter, setStatusFilter] = useState([]);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [depFrom, setDepFrom] = useState("");
  const [depTo, setDepTo] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const statusDropdownRef = useRef(null);
  const [searchParams] = useSearchParams();
  
  // Track if current filters came from URL parameters (don't persist these)
  const [filtersFromURL, setFiltersFromURL] = useState(false);
  useEffect(() => {
    let initialStatusFilter = [];
    let initialSearch = "";
    let initialPage = 1;
    let initialLimit = 10;
    let initialSortBy = "createdAt";
    let initialSortOrder = "desc";
    let initialDateFrom = "";
    let initialDateTo = "";
    let initialDepFrom = "";
    let initialDepTo = "";
    let initialSelectedUserId = "";
    let fromURL = false;

    // First, check for URL parameters (these take priority and won't be persisted)
    if (searchParams.size > 0) {
      fromURL = true;
      
      // Parse URL parameters
      if (searchParams.get('search')) {
        initialSearch = searchParams.get('search');
        setSearch(initialSearch);
      }
      
      if (searchParams.get('page')) {
        const pageParam = parseInt(searchParams.get('page'));
        if (!isNaN(pageParam) && pageParam > 0) {
          initialPage = pageParam;
          setPage(initialPage);
        }
      }
      
      if (searchParams.get('limit')) {
        const limitParam = parseInt(searchParams.get('limit'));
        if (!isNaN(limitParam) && [10, 15, 20, 50].includes(limitParam)) {
          initialLimit = limitParam;
          setLimit(initialLimit);
        }
      }
      
      if (searchParams.get('sortBy')) {
        initialSortBy = searchParams.get('sortBy');
        setSortBy(initialSortBy);
      }
      
      if (searchParams.get('sortOrder')) {
        const orderParam = searchParams.get('sortOrder');
        if (['asc', 'desc'].includes(orderParam)) {
          initialSortOrder = orderParam;
          setSortOrder(initialSortOrder);
        }
      }
      
      if (searchParams.get('dateFrom')) {
        initialDateFrom = searchParams.get('dateFrom');
        setDateFrom(initialDateFrom);
      }
      
      if (searchParams.get('dateTo')) {
        initialDateTo = searchParams.get('dateTo');
        setDateTo(initialDateTo);
      }
      
      if (searchParams.get('depFrom')) {
        initialDepFrom = searchParams.get('depFrom');
        setDepFrom(initialDepFrom);
      }
      
      if (searchParams.get('depTo')) {
        initialDepTo = searchParams.get('depTo');
        setDepTo(initialDepTo);
      }
      
      if (searchParams.get('userId')) {
        initialSelectedUserId = searchParams.get('userId');
        setSelectedUserId(initialSelectedUserId);
      }
      
      if (searchParams.get('status')) {
        const statusParam = searchParams.get('status');
        try {
          // Handle both single status and comma-separated multiple statuses
          const statusArray = statusParam.includes(',') 
            ? statusParam.split(',').map(s => s.trim()).filter(Boolean)
            : [statusParam];
          
          if (statusArray.length > 0) {
            initialStatusFilter = statusArray;
            setStatusFilter(initialStatusFilter);
          }
        } catch (e) {
          console.warn('Invalid status parameter:', statusParam);
        }
      }
      
      setFiltersFromURL(true);
    } else {
      // No URL parameters, check session storage
      const persisted = sessionStorage.getItem("bookingFilters");
      if (persisted) {
        try {
          const p = JSON.parse(persisted);
          initialSearch = p.search || "";
          initialPage = p.page || 1;
          initialLimit = p.limit || 10;
          initialSortBy = p.sortBy || "createdAt";
          initialSortOrder = p.sortOrder || "desc";
          initialDateFrom = p.dateFrom || "";
          initialDateTo = p.dateTo || "";
          initialDepFrom = p.depFrom || "";
          initialDepTo = p.depTo || "";
          initialSelectedUserId = p.selectedUserId || "";
          initialStatusFilter = p.statusFilter || [];
          
          setSearch(initialSearch);
          setPage(initialPage);
          setLimit(initialLimit);
          setSortBy(initialSortBy);
          setSortOrder(initialSortOrder);
          setDateFrom(initialDateFrom);
          setDateTo(initialDateTo);
          setDepFrom(initialDepFrom);
          setDepTo(initialDepTo);
          setSelectedUserId(initialSelectedUserId);
          setStatusFilter(initialStatusFilter);
        } catch (e) {
          console.warn('Error parsing persisted filters:', e);
        }
      }
      
      setFiltersFromURL(false);
    }

    // Build initial payload for API call
    const initialPayload = {
      search: initialSearch,
      page: initialPage,
      limit: initialLimit,
      sort_by: initialSortBy,
      sort_order: initialSortOrder,
    };
    
    if (initialDateFrom) initialPayload.date_from = initialDateFrom;
    if (initialDateTo) initialPayload.date_to = initialDateTo;
    if (initialDepFrom) initialPayload.departure_from = initialDepFrom;
    if (initialDepTo) initialPayload.departure_to = initialDepTo;
    if (initialSelectedUserId) initialPayload.user_id = initialSelectedUserId;
    if (Array.isArray(initialStatusFilter) && initialStatusFilter.length) initialPayload.status = initialStatusFilter;
    
    dispatch(bookinlist(initialPayload));
  }, [searchParams, dispatch]);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const list = useSelector((state) => state.Booking.booking);
  const handlePageClick = (e) => {
    setPage(e.selected + 1);
    setFiltersFromURL(false); // Reset URL flag when user manually changes page
    const payload = {
      search,
      page: e.selected + 1,
      limit,
      sort_by: sortBy,
      sort_order: sortOrder,
    };
    if (dateFrom) payload.date_from = dateFrom;
    if (dateTo) payload.date_to = dateTo;
    if (depFrom) payload.departure_from = depFrom;
    if (depTo) payload.departure_to = depTo;
    if (selectedUserId) payload.user_id = selectedUserId;
    if (Array.isArray(statusFilter) && statusFilter.length) payload.status = statusFilter;
    dispatch(bookinlist(payload));
    persistFilters();
  };

  const handelsearch = () => {
    setFiltersFromURL(false); // Reset URL flag when user manually searches
    if (search !== "") {
      const payload = {
        search,
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (dateFrom) payload.date_from = dateFrom;
      if (dateTo) payload.date_to = dateTo;
      if (depFrom) payload.departure_from = depFrom;
      if (depTo) payload.departure_to = depTo;
      if (selectedUserId) payload.user_id = selectedUserId;
      if (Array.isArray(statusFilter) && statusFilter.length) payload.status = statusFilter;
      dispatch(bookinlist(payload));
    } else {
      const payload = {
        search: "",
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (dateFrom) payload.date_from = dateFrom;
      if (dateTo) payload.date_to = dateTo;
      if (depFrom) payload.departure_from = depFrom;
      if (depTo) payload.departure_to = depTo;
      if (selectedUserId) payload.user_id = selectedUserId;
      if (Array.isArray(statusFilter) && statusFilter.length) payload.status = statusFilter;
      dispatch(bookinlist(payload));
    }
    persistFilters();
  };

  const exportcsv = async () => {
    const header = [
      {
        id: "booking_number",
        title: "Booking Number",
      },
      {
        id: "care_seeker",
        title: "Care Seeker Name",
      },
      {
        id: "care_seeker_email",
        title: "Care Seeker Email",
      },
      {
        id: "care_seeker_phone",
        title: "Care Seeker Phone",
      },
      {
        id: "booking_status",
        title: "Booking Status",
      },
      {
        id: "flight_number",
        title: "Flight Number",
      },
      {
        id: "departure_date",
        title: "Departure Date",
      },
      {
        id: "departure_airport",
        title: "Departure Airport",
      },
      {
        id: "destination_airport",
        title: "Destination Airport",
      },
      {
        id: "plan_type",
        title: "Plan Type",
      },
      {
        id: "total_amount",
        title: "Total Amount",
      },
      {
        id: "payment_status",
        title: "Payment Status",
      },
      {
        id: "createdAt",
        title: "Created At",
      },
    ];

    // Build export payload according to backend validation requirements
    const exportPayload = {
      header,
      status: statusFilter.length > 0 ? statusFilter[0] : "payment_confirmed", // Backend expects single status string
    };

    // Add optional filters only if they have values
    if (dateFrom) exportPayload.date_from = dateFrom;
    if (dateTo) exportPayload.date_to = dateTo;
    if (depFrom) exportPayload.departure_from = depFrom;
    if (depTo) exportPayload.departure_to = depTo;
    if (selectedUserId) exportPayload.user_id = selectedUserId;

    // Custom export function that doesn't show toast errors
    const tryServerExport = async (payload) => {
      try {
        const response = await fetch('/api/v1/admin/export-booking', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': localStorage.getItem('authorization') ? `Bearer ${localStorage.getItem('authorization')}` : ''
          },
          body: JSON.stringify(payload)
        });
        
        if (response.ok) {
          const data = await response.json();
          return data.data;
        }
        return null;
      } catch (error) {
        return null;
      }
    };

    try {
      setExportLoading(true);
      
      // Try server export first
      const serverResult = await tryServerExport(exportPayload);
      
      if (serverResult) {
        // Server export successful
        const link = document.createElement('a');
        link.href = serverResult;
        link.download = `bookings_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Server export failed, use client-side export
        console.log('Server export failed, using client-side export');
        exportToCSVFallback(list?.data || [], `bookings_export_${new Date().toISOString().split('T')[0]}.csv`);
      }
    } catch (error) {
      console.log('Export error, using client-side export:', error.message);
      // Fallback to client-side export
      exportToCSVFallback(list?.data || [], `bookings_export_${new Date().toISOString().split('T')[0]}.csv`);
    } finally {
      setExportLoading(false);
    }
  };

  function convertToCapitalized(str) {
    return str
      .split("_") // Split by underscores
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
      .join(" "); // Join the words with a space
  }

  // Modern clear search
  const clearSearch = () => {
    setSearch("");
    setPage(1);
    setStatusFilter([]);
    setDateFrom("");
    setDateTo("");
    setDepFrom("");
    setDepTo("");
    setSelectedUserId("");
    setSortBy("createdAt");
    setSortOrder("desc");
    setLoading(true);
    setFiltersFromURL(false); // Reset URL flag when clearing
    sessionStorage.removeItem("bookingFilters");
    dispatch(
      bookinlist({
        search: "",
        page: 1,
        limit,
        sort_by: "createdAt",
        sort_order: "desc",
      })
    ).then(() => setLoading(false));
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handelsearch();
    }
  };

  const persistFilters = () => {
    // Don't persist filters if they came from URL parameters
    if (filtersFromURL) {
      return;
    }
    
    sessionStorage.setItem("bookingFilters", JSON.stringify({
      search, page, limit, statusFilter, sortBy, sortOrder, dateFrom, dateTo, depFrom, depTo, selectedUserId
    }));
  };

  const fetchSuggestions = async (q) => {
    if (!q) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      setSuggestionLoading(true);
      const res = await fetch(`/booking/search-suggestions?q=${encodeURIComponent(q)}&type=all&limit=8`, {
        headers: { Authorization: localStorage.getItem("authorization") ? `Bearer ${localStorage.getItem("authorization")}` : "" }
      });
      const json = await res.json();
      const data = json?.data || {};
      const combined = [
        ...(data.bookings || []).map(b => ({ type: "booking", label: `#${b.booking_number}`, value: b.booking_number })),
        ...(data.users || []).map(u => ({ type: "user", label: `${u.first_name} ${u.last_name} (${u.email || "N/A"})`, value: u._id }))
      ];
      setSuggestions(combined);
      setShowSuggestions(true);
    } catch (err) {
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setSuggestionLoading(false);
    }
  };

  // Status options for booking
  const statusOptions = [
    { label: "Booking Request Initiated", value: "booking_request_initiated" },
    { label: "Payment Confirmed", value: "payment_confirmed" },
    { label: "Accepted By Carer", value: "accepted_by_carer" },
    { label: "Carer Assigned", value: "carer_assigned" },
    { label: "Completed", value: "completed" },
    { label: "Cancellation Pending", value: "cancellation_pending" },
    { label: "Cancellation Accepted", value: "cancellation_accepted" },
    { label: "Cancellation Completed", value: "cancellation_completed" }
  ];

  // Handle status selection
  const handleStatusSelect = (status) => {
    let newStatusFilter;
    if (statusFilter.includes(status)) {
      newStatusFilter = statusFilter.filter(s => s !== status);
    } else {
      newStatusFilter = [...statusFilter, status];
    }
    setStatusFilter(newStatusFilter);
    setFiltersFromURL(false); // Reset URL flag when user manually changes filters
    
    const payload = {
      search,
      page,
      limit,
      sort_by: sortBy,
      sort_order: sortOrder,
    };
    if (dateFrom) payload.date_from = dateFrom;
    if (dateTo) payload.date_to = dateTo;
    if (depFrom) payload.departure_from = depFrom;
    if (depTo) payload.departure_to = depTo;
    if (selectedUserId) payload.user_id = selectedUserId;
    if (newStatusFilter.length) payload.status = newStatusFilter;
    dispatch(bookinlist(payload));
    persistFilters();
  };

  // Handle status removal
  const handleStatusRemove = (status) => {
    const newStatusFilter = statusFilter.filter(s => s !== status);
    setStatusFilter(newStatusFilter);
    setFiltersFromURL(false); // Reset URL flag when user manually changes filters
    
    const payload = {
      search,
      page,
      limit,
      sort_by: sortBy,
      sort_order: sortOrder,
    };
    if (dateFrom) payload.date_from = dateFrom;
    if (dateTo) payload.date_to = dateTo;
    if (depFrom) payload.departure_from = depFrom;
    if (depTo) payload.departure_to = depTo;
    if (selectedUserId) payload.user_id = selectedUserId;
    if (newStatusFilter.length) payload.status = newStatusFilter;
    dispatch(bookinlist(payload));
    persistFilters();
  };

  // Helper function to get active filters summary
  const getActiveFilters = () => {
    const filters = [];
    if (search) filters.push(`Search: "${search}"`);
    if (statusFilter.length > 0) filters.push(`Status: ${statusFilter.length} selected`);
    if (dateFrom) filters.push(`From: ${new Date(dateFrom).toLocaleDateString()}`);
    if (dateTo) filters.push(`To: ${new Date(dateTo).toLocaleDateString()}`);
    if (depFrom) filters.push(`Dep From: ${new Date(depFrom).toLocaleDateString()}`);
    if (depTo) filters.push(`Dep To: ${new Date(depTo).toLocaleDateString()}`);
    return filters;
  };

  const activeFilters = getActiveFilters();
  const hasActiveFilters = activeFilters.length > 0;

  // Fallback client-side CSV export function
  const exportToCSVFallback = (data, filename) => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    const csvHeaders = [
      'Booking Number',
      'Care Seeker Name',
      'Care Seeker Email',
      'Care Seeker Phone',
      'Booking Status',
      'Flight Number',
      'Departure Date',
      'Departure Airport',
      'Destination Airport',
      'Plan Type',
      'Total Amount',
      'Payment Status',
      'Created At'
    ];

    const csvRows = data.map(booking => [
      booking.booking_number || '',
      booking.care_seeker ? `${booking.care_seeker.first_name} ${booking.care_seeker.last_name}` : '',
      booking.care_seeker?.email || '',
      booking.care_seeker?.phone ? `${booking.care_seeker.phone.country_code} ${booking.care_seeker.phone.number}` : '',
      booking.booking_status ? booking.booking_status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : '',
      booking.flight_details?.flight_number || '',
      booking.flight_details?.departure_date ? new Date(booking.flight_details.departure_date).toLocaleDateString() : '',
      booking.flight_details?.departure_airport || '',
      booking.flight_details?.destination_airport || '',
      booking.plan_type || '',
      booking.payment_details?.total_amount ? `$${booking.payment_details.total_amount.toFixed(2)}` : '',
      booking.payment_details?.payment_status || '',
      booking.createdAt ? new Date(booking.createdAt).toLocaleDateString() : ''
    ]);

    const csvContent = [csvHeaders, ...csvRows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <LoadingOverlay active={loading} text="Loading data...">
      <Layout>
        <ContentWrapper title="Bookings">
          {/* Collapsible Filter Bar */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
            {/* Filter Header - Always Visible */}
            <div 
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setFiltersExpanded(!filtersExpanded)}
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                  {!filtersExpanded && hasActiveFilters && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {activeFilters.map((filter, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-emerald-100 text-emerald-800">
                          {filter}
                        </span>
                      ))}
                    </div>
                  )}
                  {!filtersExpanded && !hasActiveFilters && (
                    <p className="text-sm text-gray-500">Click to expand filter options</p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="text-sm text-gray-500">
                  {list?.totalItems || 0} total bookings
                </div>
                <svg 
                  className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${filtersExpanded ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Expandable Filter Content */}
            {filtersExpanded && (
              <div className="px-4 pb-4 border-t border-gray-200">
                {/* Search Bar with Suggestions */}
                <div className="mb-4 mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      Search Bookings
                    </span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search by booking ID or care seeker name..."
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        fetchSuggestions(e.target.value);
                        if (e.target.value === "") {
                          clearSearch();
                        }
                      }}
                      onKeyDown={handleSearchKeyDown}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    {search && (
                      <button
                        onClick={clearSearch}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                    
                    {showSuggestions && suggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                        {suggestionLoading ? (
                          <div className="p-3 text-gray-500 flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-500 mr-2"></div>
                            Loading suggestions...
                          </div>
                        ) : (
                          suggestions.map((s, i) => (
                            <div
                              key={i}
                              onClick={() => {
                                if (s.type === "booking") {
                                  setSearch(s.value);
                                  setSelectedUserId("");
                                } else {
                                  setSelectedUserId(s.value);
                                }
                                setShowSuggestions(false);
                                handelsearch();
                              }}
                              className="p-3 cursor-pointer hover:bg-emerald-50 border-b border-gray-100 last:border-b-0 transition-colors"
                            >
                              <div className="flex items-center">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mr-2 ${
                                  s.type === "booking" 
                                    ? "bg-blue-100 text-blue-800" 
                                    : "bg-green-100 text-green-800"
                                }`}>
                                  {s.type === "booking" ? "Booking" : "Care Seeker"}
                                </span>
                                <span className="text-gray-900">{s.label}</span>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Filter Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Booking Status
                      </span>
                    </label>
                    <div className="relative" ref={statusDropdownRef}>
                      <div
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white cursor-pointer focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 flex items-center justify-between min-h-[42px] transition-colors"
                        onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                      >
                        <div className="flex flex-wrap gap-1">
                          {statusFilter.length === 0 && (
                            <span className="text-gray-500">Select booking status...</span>
                          )}
                          {statusFilter.map((status) => {
                            const opt = statusOptions.find((o) => o.value === status);
                            return (
                              <span key={status} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-emerald-100 text-emerald-800">
                                {opt?.label}
                                <button
                                  className="ml-1 text-emerald-600 hover:text-emerald-800 transition-colors"
                                  onClick={(e) => { e.stopPropagation(); handleStatusRemove(status); }}
                                >
                                  ×
                                </button>
                              </span>
                            );
                          })}
                        </div>
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                      {statusDropdownOpen && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                          {statusOptions.map((opt) => (
                            <div
                              key={opt.value}
                              className={`px-3 py-2 cursor-pointer hover:bg-emerald-50 flex items-center justify-between transition-colors ${
                                statusFilter.includes(opt.value) ? "bg-emerald-50 text-emerald-700" : "text-gray-700"
                              }`}
                              onClick={(e) => { e.stopPropagation(); handleStatusSelect(opt.value); }}
                            >
                              <span className="flex items-center">
                                <div className={`w-2 h-2 rounded-full mr-2 ${
                                  opt.value === 'completed' ? 'bg-green-500' :
                                  opt.value === 'carer_assigned' ? 'bg-blue-500' :
                                  opt.value === 'payment_confirmed' ? 'bg-emerald-500' :
                                  opt.value.includes('cancellation') ? 'bg-red-500' : 'bg-yellow-500'
                                }`}></div>
                                {opt.label}
                              </span>
                              {statusFilter.includes(opt.value) && (
                                <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Click to select multiple statuses</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Created From
                      </span>
                    </label>
                    <input 
                      type="date" 
                      value={dateFrom} 
                      onChange={(e) => { setDateFrom(e.target.value); handelsearch(); }} 
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors" 
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Created To
                      </span>
                    </label>
                    <input 
                      type="date" 
                      value={dateTo} 
                      onChange={(e) => { setDateTo(e.target.value); handelsearch(); }} 
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors" 
                    />
                  </div>
                </div>
                
                {/* Additional Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        Departure From
                      </span>
                    </label>
                    <input 
                      type="date" 
                      value={depFrom} 
                      onChange={(e) => { setDepFrom(e.target.value); handelsearch(); }} 
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors" 
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        Departure To
                      </span>
                    </label>
                    <input 
                      type="date" 
                      value={depTo} 
                      onChange={(e) => { setDepTo(e.target.value); handelsearch(); }} 
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors" 
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707L16.414 6.5A1 1 0 0015.707 6H7a2 2 0 00-2 2v11a2 2 0 002 2z" />
                        </svg>
                        Items per Page
                      </span>
                    </label>
                    <select 
                      value={limit} 
                      onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); handelsearch(); }} 
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors bg-white"
                    >
                      {[10,15,20,50].map(n => (<option key={n} value={n}>{n} items</option>))}
                    </select>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-3">
                    <button
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                      onClick={clearSearch}
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Clear All Filters
                    </button>
                    
                    {hasActiveFilters && (
                      <div className="text-sm text-gray-500">
                        {activeFilters.length} filter{activeFilters.length !== 1 ? 's' : ''} active
                      </div>
                    )}
                  </div>

                  <button 
                    className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    onClick={exportcsv}
                    disabled={exportLoading}
                  >
                    {exportLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export CSV ({list?.data?.length || 0})
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Enhanced Bookings Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Table Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-emerald-100">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <svg className="w-5 h-5 mr-2 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Bookings ({list?.totalItems || 0})
              </h3>
            </div>

            {/* Table Content */}
            <div className="overflow-x-auto">
              <table className="w-full responsive-table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th 
                      className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-emerald-600 transition-colors" 
                      onClick={() => { setSortBy("booking_number"); setSortOrder(sortOrder === "asc" ? "desc" : "asc"); handelsearch(); }}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Booking ID</span>
                        {sortBy === "booking_number" && (
                          <svg className={`w-4 h-4 ${sortOrder === "asc" ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Customer
                    </th>
                    <th 
                      className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-emerald-600 transition-colors" 
                      onClick={() => { setSortBy("createdAt"); setSortOrder(sortOrder === "asc" ? "desc" : "asc"); handelsearch(); }}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Booking Date</span>
                        {sortBy === "createdAt" && (
                          <svg className={`w-4 h-4 ${sortOrder === "asc" ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-emerald-600 transition-colors" 
                      onClick={() => { setSortBy("flight_details.departure_date"); setSortOrder(sortOrder === "asc" ? "desc" : "asc"); handelsearch(); }}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Travel Date</span>
                        {sortBy === "flight_details.departure_date" && (
                          <svg className={`w-4 h-4 ${sortOrder === "asc" ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Payment
                    </th>
                    <th 
                      className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-emerald-600 transition-colors" 
                      onClick={() => { setSortBy("booking_status"); setSortOrder(sortOrder === "asc" ? "desc" : "asc"); handelsearch(); }}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {sortBy === "booking_status" && (
                          <svg className={`w-4 h-4 ${sortOrder === "asc" ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {list?.data?.map(function (object, i) {
                    return (
                      <tr
                        key={object?._id || i}
                        className="hover:bg-gray-50 transition-colors duration-200 group"
                      >
                        {/* Booking ID */}
                        <td data-label="Booking ID" className="px-6 py-4">
                          <div className="text-sm font-semibold text-gray-900">
                            #{object?.booking_number}
                          </div>
                          <div className="text-xs text-gray-500">
                            {moment(object?.createdAt).format("hh:mm A")}
                          </div>
                        </td>

                        {/* Customer */}
                        <td data-label="Customer" className="px-6 py-4">
                          <a
                            href={object?.care_seeker?._id ? `/users/${object.care_seeker._id}` : undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-3 hover:text-emerald-600 transition-colors"
                          >
                            <div className="relative">
                              <img
                                src={object?.care_seeker?.profile_picture || defaultAvatar}
                                alt="profile"
                                onError={(e) => { e.currentTarget.src = defaultAvatar; }}
                                className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-200 group-hover:ring-emerald-300 transition-all duration-200"
                              />
                              {object?.care_seeker?.shufti_verification && (
                                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {FirstLetterCapital(
                                  object?.care_seeker?.first_name +
                                    " " +
                                    object?.care_seeker?.last_name
                                )}
                              </p>
                              <p className="text-xs text-gray-500">
                                Care Seeker
                              </p>
                            </div>
                          </a>
                        </td>

                        {/* Booking Date */}
                        <td data-label="Booking Date" className="px-6 py-4">
                          <div className="text-sm text-gray-900 font-medium">
                            {moment(object?.createdAt).format("MMM D, YYYY")}
                          </div>
                          <div className="text-xs text-gray-500">
                            {moment(object?.createdAt).fromNow()}
                          </div>
                        </td>

                        {/* Travel Date */}
                        <td data-label="Travel Date" className="px-6 py-4">
                          <div className="text-sm text-gray-900 font-medium flex items-center gap-1">
                            <span>
                              {object?.flight_details?.departure_date
                                ? moment(object.flight_details.departure_date).format("MMM D, YYYY")
                                : 'Not set'}
                            </span>
                            {object?.flight_details?.is_manual_flight && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800" title="Local / Manual Flight">
                                ⚠️ Local
                              </span>
                            )}
                          </div>
                          {object?.flight_details?.departure_date && (
                            <div className="text-xs text-gray-500">
                              {moment(object.flight_details.departure_date).fromNow()}
                            </div>
                          )}
                        </td>

                        {/* Payment */}
                        <td data-label="Payment" className="px-6 py-4">
                          {object?.payment_details ? (
                            <div className="space-y-1">
                              <div className="text-sm font-semibold text-gray-900">
                                {object.payment_details.total_amount?.toLocaleString("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                })}
                              </div>
                              {Number(object.payment_details.subtotal || 0) > 0 && (
                                <div className="text-xs text-gray-500">
                                  Subtotal: ${Number(object.payment_details.subtotal).toFixed(2)}
                                </div>
                              )}
                              {Number(object.payment_details.discount || 0) > 0 && (
                                <div className="text-xs text-emerald-600 font-medium">
                                  Discount: -${Number(object.payment_details.discount).toFixed(2)}
                                </div>
                              )}
                              {Number(object.payment_details.low_order_fee || 0) > 0 && (
                                <div className="text-xs text-blue-600 font-medium">
                                  Min Floor: +${Number(object.payment_details.low_order_fee).toFixed(2)}
                                </div>
                              )}
                              {(() => {
                                const isConfirmed = object.payment_details?.payment_status === 'confirmed' || 
                                                    object.booking_status === 'payment_confirmed' ||
                                                    object.booking_status === 'confirmed' ||
                                                    object.booking_status === 'accepted_by_carer' ||
                                                    object.booking_status === 'assigned' ||
                                                    object.booking_status === 'completed';
                                const displayStatus = isConfirmed ? 'Confirmed' : FirstLetterCapital(object.payment_details?.payment_status || 'Pending');
                                return (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    isConfirmed 
                                      ? 'bg-green-100 text-green-800' 
                                      : object.payment_details?.payment_status === 'failed'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {displayStatus}
                                  </span>
                                );
                              })()}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">No payment info</span>
                          )}
                        </td>

                        {/* Status */}
                        <td data-label="Status" className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            object?.booking_status === 'accepted_by_carer' || object?.booking_status === 'confirmed'
                              ? 'bg-green-100 text-green-800'
                              : object?.booking_status === 'cancelled' || object?.booking_status === 'blocked'
                              ? 'bg-red-100 text-red-800'
                              : object?.booking_status === 'pending' || object?.booking_status === 'cancellation_pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {convertToCapitalized(object?.booking_status)}
                          </span>
                        </td>

                        {/* Actions */}
                        <td data-label="Actions" className="px-6 py-4">
                          <div className="flex items-center justify-center space-x-2">
                            {/* View Button */}
                            <button
                              onClick={() => navigate(`/booking-details/${object?._id}`)}
                              className="group relative p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                              title="View Booking Details"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                View Details
                              </span>
                            </button>

                            {/* Accept Cancellation Button */}
                            {object?.booking_status === "cancellation_pending" && (
                              <button
                                onClick={() => {
                                  try {
                                    dispatch(
                                      acceptCancellation({ booking_id: object?._id })
                                    ).then(() => {
                                      handelsearch();
                                    });
                                  } catch (e) {}
                                }}
                                className="group relative px-3 py-1 text-xs font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors duration-200"
                              >
                                Accept Cancellation
                                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                  Approve cancellation request
                                </span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Empty State */}
            {(!list?.data || list.data.length === 0) && (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No bookings found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {search || statusFilter.length > 0 || dateFrom || dateTo
                    ? "Try adjusting your search or filter criteria."
                    : "No bookings have been made yet."}
                </p>
              </div>
            )}
          </div>
          
          <Pagination
            handlePageClick={handlePageClick}
            pageCount={Math.ceil((list?.totalItems || 0) / (limit || 10))}
          />
        </ContentWrapper>
        <ReactTooltip id="verification-tip" place="top" />
      </Layout>
    </LoadingOverlay>
  );
}
